import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, TrendingUp, Zap, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useState } from 'react';

export default function UsageWarningBanner({ subscription, t }) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  if (!subscription || dismissed) return null;

  const { usage } = subscription;
  
  // Calculate usage percentages
  const projectUsage = usage.projects_limit === -1 
    ? 0 
    : (usage.projects_count / usage.projects_limit) * 100;
    
  const aiUsage = usage.ai_generations_limit === -1 
    ? 0 
    : (usage.ai_generations_used / usage.ai_generations_limit) * 100;

  // Check if any usage is at 80% or higher
  const isProjectsWarning = projectUsage >= 80;
  const isAIWarning = aiUsage >= 80;
  const isProjectsCritical = projectUsage >= 100;
  const isAICritical = aiUsage >= 100;
  
  const showWarning = isProjectsWarning || isAIWarning;
  const isCritical = isProjectsCritical || isAICritical;
  
  if (!showWarning) return null;

  const getWarningMessage = () => {
    if (isProjectsCritical && isAICritical) {
      return t('usage_both_limit_reached') || "You've reached both your project and AI generation limits!";
    }
    if (isProjectsCritical) {
      return t('usage_projects_limit_reached') || "You've reached your project limit!";
    }
    if (isAICritical) {
      return t('usage_ai_limit_reached') || "You've reached your AI generation limit!";
    }
    if (isProjectsWarning && isAIWarning) {
      return t('usage_both_approaching') || "You're approaching both your project and AI generation limits.";
    }
    if (isProjectsWarning) {
      return t('usage_projects_approaching') || "You're running low on available projects.";
    }
    if (isAIWarning) {
      return t('usage_ai_approaching') || "You're running low on AI generations.";
    }
    return '';
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className={`mb-6 rounded-lg border ${
          isCritical 
            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' 
            : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
        }`}
      >
        <div className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-full ${
                isCritical 
                  ? 'bg-red-100 dark:bg-red-900/40' 
                  : 'bg-amber-100 dark:bg-amber-900/40'
              }`}>
                {isCritical ? (
                  <AlertTriangle className={`w-5 h-5 ${
                    isCritical ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'
                  }`} />
                ) : (
                  <TrendingUp className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                )}
              </div>
              <div className="flex-1">
                <h4 className={`font-semibold ${
                  isCritical 
                    ? 'text-red-900 dark:text-red-100' 
                    : 'text-amber-900 dark:text-amber-100'
                }`}>
                  {isCritical 
                    ? (t('usage_limit_reached') || 'Usage Limit Reached') 
                    : (t('usage_warning') || 'Approaching Limits')}
                </h4>
                <p className={`text-sm mt-1 ${
                  isCritical 
                    ? 'text-red-700 dark:text-red-300' 
                    : 'text-amber-700 dark:text-amber-300'
                }`}>
                  {getWarningMessage()}
                </p>
                
                {/* Usage bars */}
                <div className="mt-3 space-y-2">
                  {(isProjectsWarning || isProjectsCritical) && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-600 dark:text-gray-400 w-20">
                        {t('projects') || 'Projects'}
                      </span>
                      <div className="flex-1">
                        <Progress 
                          value={Math.min(projectUsage, 100)} 
                          className={`h-2 ${isProjectsCritical ? '[&>div]:bg-red-500' : '[&>div]:bg-amber-500'}`}
                        />
                      </div>
                      <span className={`text-xs font-medium ${
                        isProjectsCritical ? 'text-red-600' : 'text-amber-600'
                      }`}>
                        {usage.projects_count}/{usage.projects_limit}
                      </span>
                    </div>
                  )}
                  
                  {(isAIWarning || isAICritical) && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-600 dark:text-gray-400 w-20">
                        {t('ai_generations') || 'AI Credits'}
                      </span>
                      <div className="flex-1">
                        <Progress 
                          value={Math.min(aiUsage, 100)} 
                          className={`h-2 ${isAICritical ? '[&>div]:bg-red-500' : '[&>div]:bg-amber-500'}`}
                        />
                      </div>
                      <span className={`text-xs font-medium ${
                        isAICritical ? 'text-red-600' : 'text-amber-600'
                      }`}>
                        {usage.ai_generations_used}/{usage.ai_generations_limit}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button 
                size="sm" 
                onClick={() => navigate('/pricing')}
                className={isCritical 
                  ? 'bg-red-600 hover:bg-red-700 text-white' 
                  : 'bg-amber-600 hover:bg-amber-700 text-white'
                }
              >
                <Zap className="w-4 h-4 mr-1" />
                {t('upgrade') || 'Upgrade'}
              </Button>
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => setDismissed(true)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
